import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { authActions, selectTokenUserId } from "../store/auth-slice";
import { selectHasUnfinishedConsultDraft } from "../store/consultOrder-slice";
import {
  pauseAdvisorOnLogout,
  runBoundedLogoutSideEffects,
} from "../services/sessionSideEffects";
import { resetPharmacyNamesCache } from "../services/pharmacyLookupService";

/**
 * ITAM-2887: the advisor logout path, shared by the nav drawer and the
 * account panel. Best-effort pause of the advisor's availability, bounded to
 * 3 s, and THEN the session is cleared. The order matters on this branch:
 * the axios request interceptor reads the token from localStorage when the
 * request is sent, and `auth/logout` clears localStorage.
 *
 * Gates (ported from the pharmacist build): logout is blocked while a call is
 * live, and must be confirmed when it would discard an unfinished consult
 * reservation or RFZO request draft.
 */
export const useAdvisorLogout = () => {
  const dispatch = useDispatch();
  const userId = useSelector(selectTokenUserId);
  const activeConsult = useSelector(
    (state: RootState) => state.videoConsult.activeConsult,
  );
  const hasUnfinishedDraft = useSelector(selectHasUnfinishedConsultDraft);
  const [loggingOut, setLoggingOut] = useState(false);

  const logout = useCallback(async () => {
    if (loggingOut) {
      return;
    }
    setLoggingOut(true);
    // Session-wide: the incoming-call alert stops accepting calls now, since
    // the `auth/logout` below would drop a call joined during the pause.
    dispatch(authActions.logoutStarted());
    try {
      if (userId != null) {
        await runBoundedLogoutSideEffects([
          (signal) => pauseAdvisorOnLogout(userId, signal),
        ]);
      }
    } finally {
      // The pharmacy list cache belongs to the session that loaded it.
      resetPharmacyNamesCache();
      dispatch(authActions.logout());
    }
  }, [dispatch, userId, loggingOut]);

  return {
    logout,
    loggingOut,
    blockedByConsult: activeConsult != null,
    hasUnfinishedDraft,
  };
};
